import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TagBadge from "@/components/TagBadge";
import { tagTypeLabel } from "@/lib/utils-format";
import { Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";
import Modal from "@/components/Modal";

const TYPE_OPTIONS = [
  { value: "event", label: "Событие", color: "#2AABEE" },
  { value: "product", label: "Продукт", color: "#10b981" },
  { value: "status", label: "Статус", color: "#3b82f6" },
  { value: "source", label: "Источник", color: "#a1a1aa" },
  { value: "custom", label: "Произвольный", color: "#a78bfa" },
];

export default function Tags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {} = new, {...t}=edit

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/tags");
    setTags(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = TYPE_OPTIONS.map((o) => ({
    ...o,
    tags: tags.filter((t) => t.tag_type === o.value),
  }));

  return (
    <div data-testid="tags-page">
      <PageHeader
        title="Теги"
        subtitle="Сегментация подписчиков. Часть тегов проставляется ботом автоматически."
        actions={
          <button
            data-testid="create-tag-btn"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md"
          >
            <Plus size={14} weight="bold" /> Создать тег
          </button>
        }
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.value}>
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: g.color }}
                />
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {g.label} ({g.tags.length})
                </div>
              </div>
              {g.tags.length === 0 ? (
                <div className="text-xs text-zinc-600">Пока нет тегов</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {g.tags.map((t) => (
                    <div
                      key={t.id}
                      className="bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2.5 flex items-center justify-between hover:border-zinc-700"
                      data-testid={`tag-row-${t.id}`}
                    >
                      <div className="min-w-0">
                        <TagBadge tag={t} />
                        {t.description && (
                          <div className="text-xs text-zinc-500 mt-1">
                            {t.description}
                          </div>
                        )}
                        <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                          {t.users_count} пользователей
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditing(t)}
                          data-testid={`edit-tag-${t.id}`}
                          className="text-zinc-500 hover:text-blue-400 p-1.5"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm("Удалить тег?"))
                              await api
                                .delete(`/tags/${t.id}`)
                                .then(load);
                          }}
                          data-testid={`delete-tag-${t.id}`}
                          className="text-zinc-500 hover:text-red-400 p-1.5"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TagModal
          tag={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TagModal({ tag, onClose, onSaved }) {
  const isNew = !tag.id;
  const [form, setForm] = useState({
    name: tag.name || "",
    tag_type: tag.tag_type || "custom",
    color: tag.color || "#3b82f6",
    description: tag.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      if (isNew) await api.post("/tags", form);
      else await api.put(`/tags/${tag.id}`, form);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={isNew ? "Новый" : "Редактирование"}
      title={isNew ? "Новый тег" : tag.name}
      testid="tag-modal"
      maxWidth="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md border border-zinc-800 hover:border-zinc-700"
          >
            Отмена
          </button>
          <button
            data-testid="save-tag-btn"
            disabled={saving || !form.name.trim()}
            onClick={save}
            className="px-3 py-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-medium"
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Название">
          <input
            data-testid="tag-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
            placeholder="vip, hot_lead, …"
          />
        </Field>
        <Field label="Тип">
          <select
            data-testid="tag-type-select"
            value={form.tag_type}
            onChange={(e) =>
              setForm({
                ...form,
                tag_type: e.target.value,
                color:
                  TYPE_OPTIONS.find((o) => o.value === e.target.value)
                    ?.color || form.color,
              })
            }
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Цвет">
          <div className="flex items-center gap-2">
            <input
              data-testid="tag-color-input"
              type="color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="h-9 w-12 bg-zinc-900 border border-zinc-800 rounded-md"
            />
            <input
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
        </Field>
        <Field label="Описание">
          <textarea
            data-testid="tag-description-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        {err && (
          <div className="text-sm text-red-400" data-testid="tag-error">
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
